import type * as Y from "yjs";
import { blocksMap } from "./ydoc";

export const GRANTS_KEY = "aiGrants";

export type Grant = { by: string; byName: string; at: number };

/**
 * Shares are per person: giving a block to your assistant never exposes it to
 * somebody else's. The key encodes the owner, with the block id last so ids
 * containing the separator stay unambiguous.
 */
const SEPARATOR = "::";

function keyFor(userId: string, blockId: string): string {
  return `${userId}${SEPARATOR}${blockId}`;
}

function splitKey(key: string): { userId: string; blockId: string } | null {
  const index = key.lastIndexOf(SEPARATOR);
  if (index < 0) return null;
  return { userId: key.slice(0, index), blockId: key.slice(index + SEPARATOR.length) };
}

export function grantsMap(doc: Y.Doc): Y.Map<Grant> {
  return doc.getMap<Grant>(GRANTS_KEY);
}

export function grantBlock(
  doc: Y.Doc,
  blockId: string,
  granter: { id: string; name: string },
  at = Date.now(),
): void {
  grantsMap(doc).set(keyFor(granter.id, blockId), { by: granter.id, byName: granter.name, at });
}

export function revokeBlock(doc: Y.Doc, blockId: string, userId: string): void {
  grantsMap(doc).delete(keyFor(userId, blockId));
}

export function revokeAllGrantsOf(doc: Y.Doc, userId: string): void {
  doc.transact(() => {
    for (const key of [...grantsMap(doc).keys()]) {
      if (splitKey(key)?.userId === userId) grantsMap(doc).delete(key);
    }
  });
}

/** A share only counts while the block itself still exists. */
export function isBlockGrantedFor(doc: Y.Doc, blockId: string, userId: string): boolean {
  return grantsMap(doc).has(keyFor(userId, blockId)) && blocksMap(doc).has(blockId);
}

export function grantedBlockIdsFor(doc: Y.Doc, userId: string): string[] {
  const ids: string[] = [];
  for (const key of grantsMap(doc).keys()) {
    const parsed = splitKey(key);
    if (parsed?.userId === userId && blocksMap(doc).has(parsed.blockId)) ids.push(parsed.blockId);
  }
  return ids;
}

export function grantsForUser(doc: Y.Doc, userId: string): Record<string, Grant> {
  const result: Record<string, Grant> = {};
  for (const [key, grant] of grantsMap(doc).entries()) {
    const parsed = splitKey(key);
    if (parsed?.userId === userId && blocksMap(doc).has(parsed.blockId)) {
      result[parsed.blockId] = grant;
    }
  }
  return result;
}

/** How many other people also gave this block to their assistant. */
export function otherGrantCount(doc: Y.Doc, blockId: string, userId: string): number {
  let count = 0;
  for (const key of grantsMap(doc).keys()) {
    const parsed = splitKey(key);
    if (parsed?.blockId === blockId && parsed.userId !== userId) count += 1;
  }
  return count;
}
